/**
 * Gantt types and two presentation helpers for the rentals dispatch board.
 *
 * THERE IS NO DATA IN THIS FILE ANY MORE, despite the name — the path is kept
 * only because RentalDispatchBoard imports from it. What used to be here was a
 * week of invented dispatch runs, a driver board, a Gantt of invented rentals
 * and a truck-movement list, all attributed to four named oil and gas
 * operators, rendered verbatim on a board whose remaining tab is live. Its two
 * consumers were the board's Trucking and Map View tabs, both removed earlier,
 * and components/dispatch/TruckingView.tsx, which nothing imported.
 *
 * Production holds 0 rental_assets, 0 rental_orders and 0 rental_dispatches, so
 * every figure it showed was invented and none of it could ever have been
 * replaced by a real one.
 */

import { toCalendarDateString } from "../lib/calendar-date";

export interface GanttBar {
  customer: string;
  location: string;
  colorClass: string;
  startDay: number;
  spanDays: number;
  /** The dispatch this bar is, as GET /surface-rentals/dispatches sent it. */
  dispatchType: string | null;
  status: string | null;
  scheduledAt: string | null;
  driver: string | null;
  truck: string | null;
  orderNumber: string | null;
  /** For the popover's links to /rentals/dispatches/:id and /rentals/orders/:id. */
  dispatchId?: string | null;
  orderId?: string | null;
}

export interface GanttAsset {
  /** The row's heading: the asset number (FT-007), or the order number on a row that names no asset. */
  name: string;
  /** The line under it: the order the movement is on. Null on an order row, which would only repeat its heading. */
  id: string | null;
  /** The asset's own name (400 bbl Frac Tank), when the API sent one. */
  title?: string | null;
  bars: GanttBar[];
}

export interface GanttCategory {
  label: string;
  assets: GanttAsset[];
}

/**
 * The palette a customer bar is coloured from.
 *
 * This was a Record keyed on five demo customer names, with `customerColor`
 * falling back to a hash for anything unrecognised. Since the board reads real
 * dispatches the keys never matched — the customer is whatever `customers.name`
 * holds — so every real customer already took the hashed branch and the keys
 * did nothing but put five operator names into the shipped bundle. It is now
 * just the list of colours it always effectively was.
 */
const CUSTOMER_PALETTE: { bg: string; border: string; text: string; dot: string }[] = [
  // Saturated fills with the one dark ink, so a bar reads in both themes. Red is
  // left out on purpose: on a board, red means something is wrong, and a
  // customer is not. Four hues; the legend names who is who.
  { bg: "bg-fi-signal-amber", border: "border-black/20", text: "text-fi-signal-ink", dot: "bg-fi-signal-amber" },
  { bg: "bg-fi-signal-blue", border: "border-black/20", text: "text-fi-signal-ink", dot: "bg-fi-signal-blue" },
  { bg: "bg-fi-signal-green", border: "border-black/20", text: "text-fi-signal-ink", dot: "bg-fi-signal-green" },
  { bg: "bg-fi-signal-slate", border: "border-black/20", text: "text-fi-signal-ink", dot: "bg-fi-signal-slate" },
];

const UNNAMED_COLOR = {
  bg: "bg-fieldiq-dark",
  border: "border-fieldiq-border",
  text: "text-fieldiq-text",
  dot: "bg-fieldiq-muted",
};

/**
 * Total by construction, and stable for a given name so a customer keeps its
 * colour between renders. An absent name gets the neutral swatch rather than
 * an arbitrary one — it is not a customer, so it should not look like one.
 */
export function customerColor(name: string | null | undefined) {
  if (!name) return UNNAMED_COLOR;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return CUSTOMER_PALETTE[hash % CUSTOMER_PALETTE.length];
}

/**
 * Colours for the customers ON SCREEN, in the order given: the first four get
 * four different hues. Hashed alone, nine names over four hues collided in
 * threes (Blackspruce, Musreau and Kaybob all slate on Apex Surface Rentals,
 * 2026-10-15), and a legend cannot say which of three slate customers a bar is.
 * Past four on screen the hues repeat; the bar carries the customer's name.
 */
export function customerColorsInView(names: readonly string[]) {
  const map = new Map<string, (typeof CUSTOMER_PALETTE)[number]>();
  for (const n of names) {
    if (n && !map.has(n)) map.set(n, CUSTOMER_PALETTE[map.size % CUSTOMER_PALETTE.length]);
  }
  return (name: string | null | undefined) => (name && map.get(name)) || customerColor(name);
}

/**
 * `todayDay` is the TENANT's today ('YYYY-MM-DD', from todayCalendarDate with
 * companies.time_zone) — the column highlighted as today is the tenant's, not
 * whatever day the viewer's device thinks it is. Null highlights nothing.
 */
export function generateDays(startDate: Date, count: number, todayDay: string | null) {
  const days = [];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  for (let i = 0; i < count; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    days.push({
      dayName: dayNames[d.getDay()],
      date: `${monthNames[d.getMonth()]} ${d.getDate()}`,
      isToday: todayDay !== null && toCalendarDateString(d) === todayDay,
    });
  }
  return days;
}
